/**
 * LegalConsentGate — modale bloquante de re-acceptation des documents légaux.
 *
 * @remarks
 * Monté globalement (cf. `BaseLayout`). Au boot, si l'utilisateur authentifié a
 * des documents dont la version courante diffère de celle acceptée
 * (`GET /api/legal/pending`), affiche une modale **non-dismissible** : cases
 * **non pré-cochées**, bouton actif seulement quand toutes les cases requises
 * sont cochées → `POST /api/legal/accept` par document.
 *
 * Couvre le trou de conformité SSO : un utilisateur Authentik n'a jamais coché
 * de case au signup (il ne passe pas par le signup local) — ce gate est le seul
 * filet qui force l'acceptation au premier accès (cf. KB SAAS-A-131 §5).
 */

import { useState } from 'react';
import { Button, Checkbox, Dialog, DialogContent, DialogTitle } from '@/shared/ui';
import type { LegalDocumentType } from '@intra-alt/shared-types';
import { useAcceptLegal, useLegalPending } from '../hooks/use-legal-consent';

const DOCUMENT_LABEL: Record<LegalDocumentType, string> = {
  cgu: "Conditions générales d'utilisation",
  privacy: 'Politique de confidentialité',
  cgv: 'Conditions générales de vente',
};

export function LegalConsentGate() {
  const { data: pending } = useLegalPending();
  const { mutateAsync: accept } = useAcceptLegal();

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Rien à bloquer : pas de données, ou aucun document en attente.
  if (!pending || pending.length === 0) return null;

  const allChecked = pending.every((doc) => checked[doc.type]);

  const handleAccept = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      for (const doc of pending) {
        await accept({ type: doc.type, version: doc.version });
      }
      // L'invalidation de la query (dans le hook) refetch `pending` → []
      // → la modale se démonte d'elle-même.
    } catch {
      setSubmitError('Une erreur est survenue. Veuillez réessayer.');
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent
        blocking
        size="lg"
        className="bg-bg-subtle max-w-[480px] gap-0 rounded-xl p-7 shadow-lg"
      >
        <DialogTitle className="text-fg mb-1 block text-lg font-bold">
          Mise à jour de nos documents légaux
        </DialogTitle>
        <p className="text-fg-muted mb-5 text-sm">
          Nos conditions ont évolué. Pour continuer à utiliser la plateforme, merci d&apos;accepter
          la nouvelle version des documents suivants.
        </p>

        <div className="mb-5 flex flex-col gap-3">
          {pending.map((doc) => (
            <label
              key={doc.type}
              htmlFor={`legal-${doc.type}`}
              className="border-border text-fg-secondary flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 text-sm"
            >
              <Checkbox
                id={`legal-${doc.type}`}
                className="mt-0.5"
                disabled={isSubmitting}
                checked={checked[doc.type] ?? false}
                onCheckedChange={(c) => {
                  setChecked((prev) => ({ ...prev, [doc.type]: c === true }));
                }}
              />
              <span>
                J&apos;accepte les{' '}
                <span className="text-fg font-semibold">{DOCUMENT_LABEL[doc.type]}</span>{' '}
                <span className="text-fg-muted">(version {doc.version})</span>.
              </span>
            </label>
          ))}
        </div>

        {submitError && (
          <p role="alert" className="text-error mb-3 text-sm">
            {submitError}
          </p>
        )}

        {/* Modale posée sur toute l'app (BaseLayout), pas seulement la zone auth :
            le bouton suit l'accent de l'utilisateur, pas la marque. */}
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!allChecked}
          loading={isSubmitting}
          loadingLabel="Enregistrement…"
          onClick={() => void handleAccept()}
        >
          Accepter et continuer
        </Button>
      </DialogContent>
    </Dialog>
  );
}
